import {
  Box,
  Button,
  Typography,
  Grid,
  Card,
  CardContent,
} from "@mui/material";
import React from "react";
import { useNavigate } from "react-router-dom";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import GroupsIcon from "@mui/icons-material/Groups";
import ComputerIcon from "@mui/icons-material/Computer";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import DownloadIcon from "@mui/icons-material/Download";
import { Images } from "../../helpers/Images";

const CodePreneursTemplate = () => {
  const navigate = useNavigate();
  const rulesFileId = "17ND0xRFb0WYq65TBO9KE1POztaQCrzQO";

  return (
    <Box
      id={"codepreneurs"}
      sx={{
        width: "100%",
        maxWidth: "1500px",
        margin: "auto",
        padding: { xs: "50px 20px", sm: "50px 60px", lg: "50px 120px" },
      }}
    >
      {/* Header Section */}
      <Box
        sx={{
          textAlign: "center",
          marginBottom: "30px",
        }}
      >
        <Typography
          variant={"h1"}
          sx={{
            fontSize: { xs: "40px", sm: "55px" },
            fontWeight: "500",
            color: "primary.darker",
            marginBottom: "10px",
          }}
        >
          CodePreneurs
        </Typography>
        <Box
          sx={{
            height: "3px",
            maxWidth: { xs: "100px", md: "250px" },
            width: "100%",
            backgroundColor: "primary.main",
            margin: "10px auto 20px auto",
          }}
        />
        <Typography
          sx={{
            fontSize: { xs: "16px", sm: "18px" },
            color: "primary.darker",
            maxWidth: "900px",
            margin: "0 auto",
            lineHeight: "1.8",
          }}
        >
          Codepreneurs is a competition established under the vision of the Code
          Challenge Championship that brings school students together in a
          creative community throughout the year.
        </Typography>

        <Box
          sx={{
            marginTop: "25px",
            display: "flex",
            justifyContent: "center",
            flexDirection: { xs: "column", md: "row" },
            alignItems: "center",
            gap: { xs: "10px", md: "20px" },
          }}
        >
          <a
            download
            href={`https://drive.google.com/uc?export=download&id=${rulesFileId}`}
            style={{ textDecoration: "none" }}
          >
            <Button
              variant="contained"
              color="primary"
              startIcon={<DownloadIcon />}
              sx={{
                fontSize: { xs: "16px", sm: "18px" },
                fontWeight: "600",
                color: "secondary.main",
                padding: { xs: "12px 30px", sm: "15px 40px" },
                textTransform: "none",
                borderRadius: "10px",
                boxShadow: "0 4px 15px rgba(106, 5, 5, 0.3)",
                "&:hover": {
                  backgroundColor: "primary.light",
                  transform: "translateY(-2px)",
                  boxShadow: "0 6px 20px rgba(106, 5, 5, 0.4)",
                },
                transition: "all 0.3s ease",
              }}
            >
              Download Rules (PDF)
            </Button>
          </a>

          <Button
            variant="contained"
            color="primary"
            size="large"
            sx={{
              fontSize: { xs: "16px", sm: "18px" },
              fontWeight: "600",
              color: "secondary.main",
              padding: { xs: "12px 40px", sm: "15px 50px" },
              textTransform: "none",
              borderRadius: "10px",
              boxShadow: "0 4px 15px rgba(106, 5, 5, 0.3)",
              "&:hover": {
                backgroundColor: "primary.light",
                transform: "translateY(-2px)",
                boxShadow: "0 6px 20px rgba(106, 5, 5, 0.4)",
              },
              transition: "all 0.3s ease",
            }}
            onClick={() => navigate("/firststage")}
          >
            Register Now
          </Button>
        </Box>
      </Box>

      {/* Section 1: About CodePreneurs */}
      <Card
        sx={{
          marginBottom: "40px",
          borderRadius: "20px",
          overflow: "hidden",
          boxShadow: "0 10px 40px rgba(0, 0, 0, 0.1)",
        }}
      >
        <Box
          sx={{
            backgroundImage: `linear-gradient(135deg, rgba(106, 5, 5, 0.9), rgba(139, 13, 13, 0.85)), url(${Images.code2})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            padding: { xs: "40px 25px", sm: "60px 50px" },
            display: "flex",
            alignItems: "flex-start",
            gap: "30px",
            flexDirection: { xs: "column", md: "row" },
          }}
        >
          <Box
            sx={{
              backgroundColor: "rgba(255, 255, 255, 0.2)",
              borderRadius: "50%",
              width: { xs: "100px", sm: "120px" },
              height: { xs: "100px", sm: "120px" },
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backdropFilter: "blur(10px)",
              border: "2px solid rgba(255, 255, 255, 0.3)",
              flexShrink: 0,
            }}
          >
            <BusinessCenterIcon
              sx={{ fontSize: { xs: 50, sm: 60 }, color: "secondary.main" }}
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: "28px", sm: "36px" },
                fontWeight: "700",
                color: "secondary.main",
                marginBottom: "20px",
              }}
            >
              About CodePreneurs
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: "15px", sm: "17px" },
                color: "secondary.main",
                lineHeight: "1.8",
                opacity: 0.95,
              }}
            >
              CodePreneurs is a unique competition that bridges the gap between
              coding skills and entrepreneurial thinking. This innovative
              platform challenges participants to develop not just technical
              solutions, but complete business models and market-ready products.
              Perfect for those who want to combine their programming expertise
              with business acumen to create real-world impact through
              technology.
            </Typography>
          </Box>
        </Box>
      </Card>

      {/* Section 2: Eligibility & Age Group */}
      <Card
        sx={{
          marginBottom: "40px",
          borderRadius: "20px",
          overflow: "hidden",
          boxShadow: "0 10px 40px rgba(0, 0, 0, 0.1)",
          background:
            "linear-gradient(135deg, rgba(106, 5, 5, 0.05), rgba(106, 5, 5, 0.1))",
        }}
      >
        <CardContent
          sx={{
            padding: { xs: "40px 25px", sm: "60px 50px" },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: "30px",
              flexDirection: { xs: "column", md: "row" },
            }}
          >
            <Box
              sx={{
                backgroundColor: "primary.main",
                borderRadius: "50%",
                width: { xs: "100px", sm: "120px" },
                height: { xs: "100px", sm: "120px" },
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <GroupsIcon
                sx={{ fontSize: { xs: 50, sm: 60 }, color: "secondary.main" }}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: "28px", sm: "36px" },
                  fontWeight: "700",
                  color: "primary.darker",
                  marginBottom: "20px",
                }}
              >
                Eligibility & Age Group
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: "15px", sm: "17px" },
                  color: "primary.darker",
                  lineHeight: "1.8",
                  marginBottom: "20px",
                }}
              >
                CodePreneurs is designed for students aged{" "}
                <strong>10 to 15 years</strong> who are passionate about
                technology and entrepreneurship. This online competition
                welcomes participants who want to showcase their coding skills
                while developing innovative software projects with real-world
                applications.
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "15px",
                  marginTop: "20px",
                }}
              >
                <Box
                  sx={{
                    backgroundColor: "primary.main",
                    color: "secondary.main",
                    padding: "10px 20px",
                    borderRadius: "8px",
                    fontSize: "14px",
                    fontWeight: "600",
                  }}
                >
                  Age: 10-15 years
                </Box>
                <Box
                  sx={{
                    backgroundColor: "primary.main",
                    color: "secondary.main",
                    padding: "10px 20px",
                    borderRadius: "8px",
                    fontSize: "14px",
                    fontWeight: "600",
                  }}
                >
                  Format: Online
                </Box>
                <Box
                  sx={{
                    backgroundColor: "primary.main",
                    color: "secondary.main",
                    padding: "10px 20px",
                    borderRadius: "8px",
                    fontSize: "14px",
                    fontWeight: "600",
                  }}
                >
                  Focus: Software Projects
                </Box>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Section 3: Theme 2026 - Intelligent Systems */}
      <Card
        sx={{
          marginBottom: "40px",
          borderRadius: "20px",
          overflow: "hidden",
          boxShadow: "0 10px 40px rgba(0, 0, 0, 0.1)",
        }}
      >
        <Box
          sx={{
            background:
              "linear-gradient(135deg, rgba(106, 5, 5, 0.9), rgba(139, 13, 13, 0.85))",
            padding: { xs: "40px 25px", sm: "60px 50px" },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: "30px",
              flexDirection: { xs: "column", md: "row" },
            }}
          >
            <Box
              sx={{
                backgroundColor: "rgba(255, 255, 255, 0.2)",
                borderRadius: "50%",
                width: { xs: "100px", sm: "120px" },
                height: { xs: "100px", sm: "120px" },
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backdropFilter: "blur(10px)",
                border: "2px solid rgba(255, 255, 255, 0.3)",
                flexShrink: 0,
              }}
            >
              <LightbulbIcon
                sx={{ fontSize: { xs: 50, sm: 60 }, color: "secondary.main" }}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: "28px", sm: "36px" },
                  fontWeight: "700",
                  color: "secondary.main",
                  marginBottom: "15px",
                }}
              >
                2026 Theme: Intelligent Systems
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: "15px", sm: "17px" },
                  color: "secondary.main",
                  lineHeight: "1.8",
                  marginBottom: "20px",
                  opacity: 0.95,
                }}
              >
                In 2026, the Codepreneurs theme will be{" "}
                <strong>"Intelligent Systems"</strong> that will let teams
                utilize their creativity and innovation to implement projects
                powered by AI with any form they need to address this theme.
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: "15px", sm: "17px" },
                  color: "secondary.main",
                  lineHeight: "1.8",
                  opacity: 0.95,
                }}
              >
                For example, they can build a <strong>web application</strong>,{" "}
                <strong>mobile application</strong>,
                <strong> desktop application</strong>, or any other form. The
                key is to leverage artificial intelligence to create intelligent
                solutions that solve real-world problems while demonstrating
                strong technical skills and innovative thinking.
              </Typography>
            </Box>
          </Box>
        </Box>
      </Card>

      {/* Section 4: Competition Format */}
      <Card
        sx={{
          marginBottom: "40px",
          borderRadius: "20px",
          overflow: "hidden",
          boxShadow: "0 10px 40px rgba(0, 0, 0, 0.1)",
          background:
            "linear-gradient(135deg, rgba(106, 5, 5, 0.05), rgba(106, 5, 5, 0.1))",
        }}
      >
        <CardContent
          sx={{
            padding: { xs: "40px 25px", sm: "60px 50px" },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: "30px",
              flexDirection: { xs: "column", md: "row" },
            }}
          >
            <Box
              sx={{
                backgroundColor: "primary.main",
                borderRadius: "50%",
                width: { xs: "100px", sm: "120px" },
                height: { xs: "100px", sm: "120px" },
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <ComputerIcon
                sx={{ fontSize: { xs: 50, sm: 60 }, color: "secondary.main" }}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: "28px", sm: "36px" },
                  fontWeight: "700",
                  color: "primary.darker",
                  marginBottom: "20px",
                }}
              >
                Competition Format
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: "15px", sm: "17px" },
                  color: "primary.darker",
                  lineHeight: "1.8",
                  marginBottom: "20px",
                }}
              >
                CodePreneurs is conducted entirely <strong>online</strong>,
                allowing participants from anywhere to join and showcase their
                software development skills. The competition focuses on{" "}
                <strong>software projects</strong>
                where teams build complete applications that demonstrate both
                technical excellence and business viability.
              </Typography>
              <Box sx={{ marginTop: "25px" }}>
                <Typography
                  sx={{
                    fontSize: { xs: "16px", sm: "18px" },
                    fontWeight: "600",
                    color: "primary.darker",
                    marginBottom: "15px",
                  }}
                >
                  Key Aspects:
                </Typography>
                {[
                  "Online competition format accessible from anywhere",
                  "Focus on complete software project development",
                  "Integration of AI and intelligent systems",
                  "Business model development alongside technical implementation",
                  "Judging based on innovation, technical quality, and market potential",
                ].map((item, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      marginBottom: "10px",
                    }}
                  >
                    <Box
                      sx={{
                        width: "6px",
                        height: "6px",
                        borderRadius: "50%",
                        backgroundColor: "primary.main",
                        marginTop: "8px",
                        marginRight: "12px",
                        flexShrink: 0,
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: { xs: "14px", sm: "16px" },
                        color: "primary.darker",
                        lineHeight: "1.6",
                      }}
                    >
                      {item}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default CodePreneursTemplate;
